// Story Component Picker candidate matching. The canvas writes the
// `required-contexts` object -- the only source there is.

import { STORY_TYPE_METADATA_FIELDS, normalizeStoryType }
  from "%/dynamic-components/render-engine/constants/story-type-metadata-fields";

export const STORY_ENTITY = "Story";
export const STORY_LEGACY_ENTITY_TYPE = "LegacyStory";

const KEY_REQUIRED_CONTEXTS = "required-contexts";
const KEY_PROVIDES_CONTEXTS = "provides-contexts";
const LEGACY_STORY_APP_NAME = "Legacy";

function parseJsonOrNull(value, describeSource) {
  try {
    return JSON.parse(value);
  } catch (error) {
    // Unparseable must not read as "no contexts" — that hides the card silently.
    // eslint-disable-next-line no-console
    console.error(`story-card-candidates: ${describeSource} is corrupt — treating as no required contexts`, error);
    return null;
  }
}

// Required contexts from either shape. Takes raw rootOptions so it runs
// against a feed row without constructing an entity.
export function readRequiredContextsFromRootOptions(rootOptions) {
  let options = rootOptions ?? {};
  if (typeof options === "string") {
    options = parseJsonOrNull(options, "rootOptions") ?? {};
  }
  if (typeof options !== "object") {
    return {};
  }

  const required = readDeclaredRequiredContexts(options);
  const surfaced = legacyStoryRequirements(options, required);
  if (!Object.keys(surfaced).length) {
    return required;
  }
  return { ...surfaced, ...required };
}

function readDeclaredRequiredContexts(options) {
  const live = options[KEY_REQUIRED_CONTEXTS];
  return live && typeof live === "object" ? live : {};
}

// Pre-required-contexts components (the seeded completion set) stamped their
// story as a uri-sourced PROVIDES entry, which can never resolve at component
// scope (components have no slug) — read it as the required-story contract
// those components were minted against ever since.
function legacyStoryRequirements(options, required) {
  const surfaced = {};
  Object.entries(readProvidesFromOptions(options)).forEach(([contextName, entry]) => {
    if (required[contextName] || entry?.entitySource !== "uri") {
      return;
    }
    if (entry.entityType !== STORY_ENTITY && entry.entityType !== STORY_LEGACY_ENTITY_TYPE) {
      return;
    }
    surfaced[contextName] = { entity: STORY_ENTITY, appName: LEGACY_STORY_APP_NAME };
  });
  return surfaced;
}

function readProvidesFromOptions(options) {
  const live = options[KEY_PROVIDES_CONTEXTS];
  return live && typeof live === "object" ? live : {};
}

// Story is spelled three ways across writers. Matching is EXACT: LegacyRssStory
// and LegacyStoryComment contain "Story" but are different entities.
export function isStoryAnchor(contextDefinition) {
  if (!contextDefinition) {
    return false;
  }
  const entity = contextDefinition.entity ?? contextDefinition.type ?? null;
  if (entity === STORY_ENTITY) {
    return true;
  }
  return contextDefinition.entityType === STORY_LEGACY_ENTITY_TYPE;
}

// Pickable when it requires a Story at all — Story PLUS another context is
// still a card; the extras resolve through Provide Required Context.
export function requiresStoryContext(requiredContexts) {
  return Object.values(requiredContexts ?? {}).some(isStoryAnchor);
}

export function storyAnchorContextNames(requiredContexts) {
  const required = requiredContexts && typeof requiredContexts === "object" ? requiredContexts : {};
  return Object.keys(required).filter(name => isStoryAnchor(required[name]));
}

// Null until the name hydrates — the components feed is shallow, and showing
// the raw id puts an internal id in front of the user.
export function toStoryCardCandidate(dynamicComponentId, name, storyType = "") {
  if (typeof name !== "string" || !name.trim()) {
    return null;
  }
  return { value: Number(dynamicComponentId), label: name, storyType };
}

// Longest key first so "Program Category Card" resolves to program_category,
// not program (every program_* key shares the program prefix).
const STORY_TYPE_KEYS_BY_LENGTH = Object.keys(STORY_TYPE_METADATA_FIELDS)
    .sort((first, second) => second.length - first.length);

// The `story-type` root option, else the name-prefix convention
// resolveStoryTypeForMetadata relies on. "" = an untyped (Default) card.
export function resolveCandidateStoryType(rootOptions, name) {
  let options = rootOptions ?? {};
  if (typeof options === "string") {
    options = parseJsonOrNull(options, "rootOptions") ?? {};
  }
  // Normalised, because a spec saved before the rename still carries the old
  // spelling — without this it falls through to name-prefix guessing.
  const explicit = typeof options === "object" ? normalizeStoryType(options["story-type"]) : "";
  if (explicit && STORY_TYPE_METADATA_FIELDS[explicit]) {
    return explicit;
  }
  const label = String(name ?? "").toLowerCase();
  return STORY_TYPE_KEYS_BY_LENGTH.find(type => label.startsWith(type.replace(/_/g, " "))) ?? "";
}

// A typed row lists only that type's cards plus its current assignment (so a
// pre-filter assignment stays replaceable); the Default row ("") lists all.
export function candidatesForRow(candidates, storyTypeKey, assignedDynamicComponentId = 0) {
  const all = candidates ?? [];
  if (!storyTypeKey) {
    return all;
  }
  return all.filter(candidate => {
    if (candidate.storyType === storyTypeKey) {
      return true;
    }
    return assignedDynamicComponentId > 0 && Number(candidate.value) === Number(assignedDynamicComponentId);
  });
}
